import type { TFunction } from "i18next";
import { z } from "zod";

export const MAX_PORT_OPERATOR_APPROVAL_NOTES_LENGTH = 500;

const parseLocalDate = (value: string) => new Date(`${value}T00:00:00`);

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const approvePortOperatorLicenseSchema = (
  t: TFunction,
  vendorLicenseExpiryDate?: string,
  requireAccessPeriod = true,
) => {
  const startDateSchema = requireAccessPeriod
    ? z
        .string()
        .min(
          1,
          t("licensing.form-validation.mandatory", {
            label: t("licensing.access-enabled-from"),
          }),
        )
        .refine((v) => parseLocalDate(v) >= startOfToday(), {
          message: t("licensing.form-validation.date-not-in-past", {
            label: t("licensing.access-enabled-from"),
          }),
        })
    : z.string().optional();

  const expiryDateSchema = requireAccessPeriod
    ? z
        .string()
        .min(
          1,
          t("licensing.form-validation.mandatory", {
            label: t("licensing.access-enabled-to"),
          }),
        )
        .refine((v) => parseLocalDate(v) >= startOfToday(), {
          message: t("licensing.form-validation.date-not-in-past", {
            label: t("licensing.access-enabled-to"),
          }),
        })
        .refine(
          (v) => {
            if (!vendorLicenseExpiryDate) return true;
            return parseLocalDate(v) <= parseLocalDate(vendorLicenseExpiryDate);
          },
          {
            message: t("licensing.form-validation.date-to-not-after-trade-license", {
              label: t("licensing.access-enabled-to"),
            }),
          },
        )
    : z.string().optional();

  return z
    .object({
      startDate: startDateSchema,
      expiryDate: expiryDateSchema,
      approvalNotes: z
        .string()
        .trim()
        .max(
          MAX_PORT_OPERATOR_APPROVAL_NOTES_LENGTH,
          t("licensing.form-validation.max-length", {
            label: t("licensing.notes"),
            max: MAX_PORT_OPERATOR_APPROVAL_NOTES_LENGTH,
          }),
        )
        .optional(),
    })
    .refine(
      (data) => {
        if (!requireAccessPeriod || !data.startDate || !data.expiryDate) return true;
        const from = parseLocalDate(data.startDate);
        const to = parseLocalDate(data.expiryDate);
        return to >= from;
      },
      {
        path: ["expiryDate"],
        message: t("licensing.form-validation.date-to-not-before-from", {
          fromLabel: t("licensing.access-enabled-from"),
          toLabel: t("licensing.access-enabled-to"),
        }),
      },
    )
    .refine(
      (data) => {
        if (!requireAccessPeriod || !vendorLicenseExpiryDate || !data.expiryDate) return true;
        return parseLocalDate(data.expiryDate) <= parseLocalDate(vendorLicenseExpiryDate);
      },
      {
        path: ["expiryDate"],
        message: t("licensing.form-validation.date-to-not-after-trade-license", {
          label: t("licensing.access-enabled-to"),
        }),
      },
    );
};

export type ApprovePortOperatorLicenseSchema = z.infer<
  ReturnType<typeof approvePortOperatorLicenseSchema>
>;
